import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Building, User, Users, CheckCircle, ArrowRight } from 'lucide-react';
import { db } from '../../services/firebase';
import { collection, addDoc, serverTimestamp } from '@firebase/firestore';
import { sendEmail } from '../../cloud-functions';

interface WaitingListModalProps {
    onClose: () => void;
    onSuccess: (name: string) => void;
}

export const WaitingListModal = ({ onClose, onSuccess }: WaitingListModalProps) => {
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        role: '' as 'tenant' | 'host' | 'both' | '',
        entityType: '' as 'individual' | 'company' | ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        
        try {
            // 1. Save to Firestore
            await addDoc(collection(db, 'waitingList'), {
                ...formData,
                createdAt: serverTimestamp(),
                source: 'web_landing_page'
            });

            // 2. Send Acknowledgement Email
            // Note: The 'sendEmail' cloud function needs to handle the 'waiting_list' template logic on the backend.
            // We proceed even if email fails to ensure the user sees the success state for the signup itself.
            try {
                await sendEmail({
                    template: 'waiting_list',
                    recipient: {
                        email: formData.email,
                        name: `${formData.firstName} ${formData.lastName}`
                    },
                    data: {
                        firstName: formData.firstName,
                        role: formData.role
                    }
                });
            } catch (emailError) {
                console.warn("Email sending failed:", emailError);
            }

            // Trigger success callback (optional toast in parent)
            onSuccess(formData.firstName);
            
            // Show local success state
            setIsSuccess(true);
        } catch (error) {
            console.error("Error processing waiting list submission:", error);
            alert("Something went wrong. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleRoleSelect = (role: 'tenant' | 'host' | 'both') => {
        setFormData(prev => ({ 
            ...prev, 
            role,
            entityType: role === 'tenant' ? '' : prev.entityType 
        }));
    };

    const isHostOrBoth = formData.role === 'host' || formData.role === 'both';
    const isFormValid = formData.firstName && 
                      formData.lastName && 
                      formData.email && 
                      formData.role && 
                      (!isHostOrBoth || formData.entityType);

    if (isSuccess) {
        return (
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-end sm:items-center justify-center sm:p-4" onClick={onClose}>
                <motion.div 
                    initial={{ opacity: 0, y: "100%" }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white rounded-t-[2.5rem] sm:rounded-[2rem] w-full max-w-md overflow-hidden shadow-2xl p-8 pt-12 sm:pt-8 relative text-center"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Mobile Drag Handle */}
                    <div className="w-12 h-1.5 bg-gray-200 rounded-full mx-auto absolute top-4 left-1/2 -translate-x-1/2 sm:hidden" />
                    
                    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                        <CheckCircle size={40} className="text-green-600" />
                    </div>
                    <h2 className="text-2xl font-bold mb-2">You're on the list!</h2>
                    <p className="text-gray-600 mb-8">
                        Thanks {formData.firstName}! We've registered your request for early access. Keep an eye on your inbox for updates.
                    </p>
                    <button 
                        onClick={onClose}
                        className="w-full py-3 bg-black text-white font-bold rounded-xl hover:bg-gray-800 transition-all flex items-center justify-center gap-2"
                    >
                        Got it <ArrowRight size={18} />
                    </button>
                </motion.div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-end sm:items-center justify-center sm:p-4" onClick={onClose}>
            <motion.div 
                initial={{ opacity: 0, y: "100%" }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: "100%" }}
                transition={{ type: "spring", damping: 25, stiffness: 200 }}
                className="bg-white pt-6 pb-8 px-6 sm:p-12 rounded-t-[2.5rem] sm:rounded-[2.5rem] w-full max-w-xl max-h-[90vh] overflow-y-auto shadow-2xl relative"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Mobile Drag Handle */}
                <div className="w-12 h-1.5 bg-gray-200 rounded-full mx-auto mb-6 sm:hidden" />

                <div className="relative">
                    <button onClick={onClose} className="absolute -top-2 sm:top-0 right-0 text-gray-400 hover:text-black p-2 bg-gray-100 rounded-full transition-colors z-10">
                        <X size={20} />
                    </button>

                    <div className="mb-8 sm:mb-10 text-left sm:pr-12">
                        <h2 className="text-2xl sm:text-3xl font-bold mb-2 sm:mb-3">Get Access</h2>
                        <p className="text-gray-500 text-sm sm:text-base">Secure your spot and be among the first to experience Coleese.</p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-bold mb-2">First Name</label>
                                <input 
                                    type="text" 
                                    required
                                    value={formData.firstName}
                                    onChange={e => setFormData({...formData, firstName: e.target.value})}
                                    className="w-full px-5 py-3 rounded-2xl bg-gray-50 border border-gray-200 focus:border-black focus:ring-1 focus:ring-black outline-none transition-all"
                                    placeholder="Enter first name"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-bold mb-2">Last Name</label>
                                <input 
                                    type="text" 
                                    required
                                    value={formData.lastName}
                                    onChange={e => setFormData({...formData, lastName: e.target.value})}
                                    className="w-full px-5 py-3 rounded-2xl bg-gray-50 border border-gray-200 focus:border-black focus:ring-1 focus:ring-black outline-none transition-all"
                                    placeholder="Enter last name"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-bold mb-2">Email Address</label>
                            <input 
                                type="email" 
                                required
                                value={formData.email}
                                onChange={e => setFormData({...formData, email: e.target.value})}
                                className="w-full px-5 py-3 rounded-2xl bg-gray-50 border border-gray-200 focus:border-black focus:ring-1 focus:ring-black outline-none transition-all"
                                placeholder="name@example.com"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-bold mb-3">What brings you to Coleese?</label>
                            <div className="grid grid-cols-3 gap-3">
                                {[
                                    { id: 'tenant', label: 'Tenant', icon: <User size={18} /> },
                                    { id: 'host', label: 'Host', icon: <Building size={18} /> },
                                    { id: 'both', label: 'Both', icon: <Users size={18} /> }
                                ].map((role) => (
                                    <button
                                        key={role.id}
                                        type="button"
                                        onClick={() => handleRoleSelect(role.id as any)}
                                        className={`flex flex-col items-center gap-2 p-4 rounded-2xl border-2 transition-all ${
                                            formData.role === role.id 
                                            ? 'border-black bg-black text-white' 
                                            : 'border-gray-100 bg-gray-50 text-gray-500 hover:border-gray-300'
                                        }`}
                                    >
                                        {role.icon}
                                        <span className="text-xs font-bold">{role.label}</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        <AnimatePresence>
                            {isHostOrBoth && (
                                <motion.div 
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: 'auto', opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    className="overflow-hidden"
                                >
                                    <label className="block text-sm font-bold mb-3">Are you listing through a company or as an individual?</label>
                                    <div className="grid grid-cols-2 gap-4">
                                        <button
                                            type="button"
                                            onClick={() => setFormData({...formData, entityType: 'individual'})}
                                            className={`p-4 rounded-2xl border-2 font-bold text-sm transition-all ${
                                                formData.entityType === 'individual' 
                                                ? 'border-black bg-black text-white' 
                                                : 'border-gray-100 bg-gray-50 text-gray-500 hover:border-gray-300'
                                            }`}
                                        >
                                            Individual
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setFormData({...formData, entityType: 'company'})}
                                            className={`p-4 rounded-2xl border-2 font-bold text-sm transition-all ${
                                                formData.entityType === 'company' 
                                                ? 'border-black bg-black text-white' 
                                                : 'border-gray-100 bg-gray-50 text-gray-500 hover:border-gray-300'
                                            }`}
                                        >
                                            Company
                                        </button>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        <button
                            type="submit"
                            disabled={!isFormValid || isSubmitting}
                            className="w-full py-4 bg-black text-white font-bold rounded-2xl shadow-xl hover:bg-gray-800 transition-all disabled:bg-gray-200 disabled:text-gray-400 disabled:shadow-none flex items-center justify-center gap-3"
                        >
                            {isSubmitting ? (
                                <>
                                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                    Processing...
                                </>
                            ) : (
                                'Get Access'
                            )}
                        </button>

                        <p className="text-[10px] text-center text-gray-400 px-4">
                            By joining, you agree to our Terms of Service and Privacy Policy. We'll send you updates about our launch and early access availability.
                        </p>
                    </form>
                </div>
            </motion.div>
        </div>
    );
};